import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CostData } from '@/types/project';
import { CHART_COLORS } from '@/lib/constants';
import { BasePieChart } from '@/components/Charts/BasePieChart';
import { formatCurrency } from '@/utils/format';
import { PieChart } from 'lucide-react';

interface CostBreakdownChartProps {
  costs: CostData;
}

export const CostBreakdownChart = memo(function CostBreakdownChart({ costs }: CostBreakdownChartProps) {
  const costComponents = [
    { key: 'labour', name: 'Labour' },
    { key: 'materials', name: 'Materials' },
    { key: 'plant', name: 'Plant' },
    { key: 'subcontractor', name: 'Subcontractors' },
    { key: 'overheads', name: 'Overheads' },
    { key: 'staff', name: 'Staff' },
  ] as const;

  const data = costComponents
    .map(({ key, name }) => ({
      name,
      value: costs[key] ? (costs[key].toDate || 0) + (costs[key].forecast || 0) : 0
    }))
    .filter(item => item.value > 0);

  const totalCosts = data.reduce((sum, item) => sum + item.value, 0);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Cost Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
            <PieChart className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-center">
              Enter cost data to see the breakdown.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          Cost Breakdown
          <span className="text-sm font-normal text-muted-foreground">
            Total: {formatCurrency(totalCosts)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <BasePieChart
            data={data}
            colors={Object.values(CHART_COLORS)}
            outerRadius={100}
            showPercentage
          />
        </div>
      </CardContent>
    </Card>
  );
});
